export interface BaseColors {
  background: string;
  foreground: string;
  muted: string;
  'muted-foreground': string;
  popover: string;
  'popover-foreground': string;
  border: string;
  input: string;
  card: string;
  'card-foreground': string;
  primary: string;
  'primary-foreground': string;
  secondary: string;
  'secondary-foreground': string;
  accent: string;
  'accent-foreground': string;
  destructive: string;
  'destructive-foreground': string;
  ring: string;
  'sidebar-background': string;
  'sidebar-foreground': string;
  'sidebar-primary': string;
  'sidebar-primary-foreground': string;
  'sidebar-accent': string;
  'sidebar-accent-foreground': string;
  'sidebar-border': string;
  'sidebar-ring': string;
  'color-1': string;
  'color-2': string;
  'color-3': string;
  'color-4': string;
  'color-5': string;
  'chart-1': string;
  'chart-2': string;
  'chart-3': string;
  'chart-4': string;
  'chart-5': string;
  radius?: string;
  'font-sans'?: string;
  'font-serif'?: string;
  'font-mono'?: string;
  'shadow-x'?: string;
  'shadow-y'?: string;
  'shadow-blur'?: string;
  'shadow-spread'?: string;
  'shadow-opacity'?: string;
  'shadow-color'?: string;
  'shadow-2xs'?: string;
  'shadow-xs'?: string;
  'shadow-sm'?: string;
  shadow?: string;
  'shadow-md'?: string;
  'shadow-lg'?: string;
  'shadow-xl'?: string;
  'shadow-2xl'?: string;
  'tracking-normal'?: string;
  spacing?: string;
}

export type ThemeMode = 'light' | 'dark';

export interface Theme {
  id: string;
  name: string;
  description?: string;
  colors: {
    light: BaseColors;
    dark: BaseColors;
  };
}

export interface ThemeConfig {
  theme: string;
  mode: ThemeMode;
}

export const themes: Record<string, Theme> = {
  default: {
    id: 'default',
    name: 'Default',
    description: 'Clean and minimal default theme',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '222.2 47.4% 11.2%',
        muted: '210 40% 96.1%',
        'muted-foreground': '215.4 16.3% 46.9%',
        popover: '0 0% 100%',
        'popover-foreground': '222.2 47.4% 11.2%',
        border: '214.3 31.8% 91.4%',
        input: '214.3 31.8% 91.4%',
        card: '0 0% 100%',
        'card-foreground': '222.2 47.4% 11.2%',
        primary: '222.2 47.4% 11.2%',
        'primary-foreground': '210 40% 98%',
        secondary: '210 40% 96.1%',
        'secondary-foreground': '222.2 47.4% 11.2%',
        accent: '210 40% 96.1%',
        'accent-foreground': '222.2 47.4% 11.2%',
        destructive: '0 100% 50%',
        'destructive-foreground': '210 40% 98%',
        ring: '215 20.2% 65.1%',
        'sidebar-background': '0 0% 98%',
        'sidebar-foreground': '240 5.3% 26.1%',
        'sidebar-primary': '240 5.9% 10%',
        'sidebar-primary-foreground': '0 0% 98%',
        'sidebar-accent': '240 4.8% 95.9%',
        'sidebar-accent-foreground': '240 5.9% 10%',
        'sidebar-border': '220 13% 91%',
        'sidebar-ring': '217.2 91.2% 59.8%',
        'color-1': '0 100% 63%',
        'color-2': '270 100% 63%',
        'color-3': '210 100% 63%',
        'color-4': '195 100% 63%',
        'color-5': '90 100% 63%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',
      },
      dark: {
        background: '190 16% 15.84%',
        foreground: '190 4% 85.9%',
        muted: '190 20% 29.4%',
        'muted-foreground': '190 4% 59.8%',
        popover: '190 57% 12.74%',
        'popover-foreground': '190 4% 99.9%',
        border: '190 34% 18%',
        input: '190 34% 20%',
        card: '190 16% 14.84%',
        'card-foreground': '190 4% 85.9%',
        primary: '200 40% 98%',
        'primary-foreground': '200.2 47.4% 1.2%',
        secondary: '190 16% 17.84%',
        'secondary-foreground': '190 4% 85%',
        accent: '190 16% 19.84%',
        'accent-foreground': '190 40% 78%',
        destructive: '0 63% 31%',
        'destructive-foreground': '190 40% 98%',
        ring: '196 34% 17%',
        'sidebar-background': '240 5.9% 10%',
        'sidebar-foreground': '240 4.8% 95.9%',
        'sidebar-primary': '224.3 76.3% 48%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '240 3.7% 15.9%',
        'sidebar-accent-foreground': '240 4.8% 95.9%',
        'sidebar-border': '240 3.7% 15.9%',
        'sidebar-ring': '217.2 91.2% 59.8%',
        'color-1': '0 100% 63%',
        'color-2': '270 100% 63%',
        'color-3': '210 100% 63%',
        'color-4': '195 100% 63%',
        'color-5': '90 100% 63%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',
      },
    },
  },
  mono: {
    id: 'mono',
    name: 'Mono',
    description: 'Monochrome Geist Mono & squared corners',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '0 0% 3.9216%',
        card: '0 0% 100%',
        'card-foreground': '0 0% 3.9216%',
        popover: '0 0% 100%',
        'popover-foreground': '0 0% 3.9216%',
        primary: '0 0% 45.0980%',
        'primary-foreground': '0 0% 98.0392%',
        secondary: '0 0% 96.0784%',
        'secondary-foreground': '0 0% 9.0196%',
        muted: '0 0% 96.0784%',
        'muted-foreground': '0 0% 44.3137%',
        accent: '0 0% 96.0784%',
        'accent-foreground': '0 0% 9.0196%',
        destructive: '357.1429 100% 45.2941%',
        'destructive-foreground': '0 0% 96.0784%',
        border: '0 0% 89.8039%',
        input: '0 0% 89.8039%',
        ring: '0 0% 63.1373%',
        'sidebar-background': '0 0% 98.0392%',
        'sidebar-foreground': '0 0% 3.9216%',
        'sidebar-primary': '0 0% 9.0196%',
        'sidebar-primary-foreground': '0 0% 98.0392%',
        'sidebar-accent': '0 0% 96.0784%',
        'sidebar-accent-foreground': '0 0% 9.0196%',
        'sidebar-border': '0 0% 89.8039%',
        'sidebar-ring': '0 0% 63.1373%',
        'color-1': '0 0% 45.0980%',
        'color-2': '0 0% 45.0980%',
        'color-3': '0 0% 45.0980%',
        'color-4': '0 0% 45.0980%',
        'color-5': '0 0% 45.0980%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0rem',
        'font-sans': 'Geist Mono, monospace',
        'font-serif': 'Geist Mono, monospace',
        'font-mono': 'Geist Mono, monospace',
        'shadow-x': '0px',
        'shadow-y': '1px',
        'shadow-blur': '0px',
        'shadow-spread': '0px',
        'shadow-opacity': '0',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'shadow-xs': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'shadow-sm': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 1px 2px -1px hsl(0 0% 0% / 0.00)',
        shadow: '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 1px 2px -1px hsl(0 0% 0% / 0.00)',
        'shadow-md': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 2px 4px -1px hsl(0 0% 0% / 0.00)',
        'shadow-lg': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 4px 6px -1px hsl(0 0% 0% / 0.00)',
        'shadow-xl': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 8px 10px -1px hsl(0 0% 0% / 0.00)',
        'shadow-2xl': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '0 0% 3.9216%',
        foreground: '0 0% 98.0392%',
        card: '0 0% 9.8039%',
        'card-foreground': '0 0% 98.0392%',
        popover: '0 0% 14.9020%',
        'popover-foreground': '0 0% 98.0392%',
        primary: '0 0% 45.0980%',
        'primary-foreground': '0 0% 98.0392%',
        secondary: '0 0% 14.9020%',
        'secondary-foreground': '0 0% 98.0392%',
        muted: '0 0% 14.9020%',
        'muted-foreground': '0 0% 63.1373%',
        accent: '0 0% 25.0980%',
        'accent-foreground': '0 0% 98.0392%',
        destructive: '358.8387 100% 69.6078%',
        'destructive-foreground': '0 0% 14.9020%',
        border: '0 0% 21.9608%',
        input: '0 0% 32.1569%',
        ring: '0 0% 45.0980%',
        'sidebar-background': '0 0% 9.0196%',
        'sidebar-foreground': '0 0% 98.0392%',
        'sidebar-primary': '0 0% 98.0392%',
        'sidebar-primary-foreground': '0 0% 9.0196%',
        'sidebar-accent': '0 0% 14.9020%',
        'sidebar-accent-foreground': '0 0% 98.0392%',
        'sidebar-border': '0 0% 100%',
        'sidebar-ring': '0 0% 32.1569%',
        'color-1': '0 0% 45.0980%',
        'color-2': '0 0% 45.0980%',
        'color-3': '0 0% 45.0980%',
        'color-4': '0 0% 45.0980%',
        'color-5': '0 0% 45.0980%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0rem',
        'font-sans': 'Geist Mono, monospace',
        'font-serif': 'Geist Mono, monospace',
        'font-mono': 'Geist Mono, monospace',
        'shadow-x': '0px',
        'shadow-y': '1px',
        'shadow-blur': '0px',
        'shadow-spread': '0px',
        'shadow-opacity': '0',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'shadow-xs': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'shadow-sm': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 1px 2px -1px hsl(0 0% 0% / 0.00)',
        shadow: '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 1px 2px -1px hsl(0 0% 0% / 0.00)',
        'shadow-md': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 2px 4px -1px hsl(0 0% 0% / 0.00)',
        'shadow-lg': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 4px 6px -1px hsl(0 0% 0% / 0.00)',
        'shadow-xl': '0px 1px 0px 0px hsl(0 0% 0% / 0.00), 0px 8px 10px -1px hsl(0 0% 0% / 0.00)',
        'shadow-2xl': '0px 1px 0px 0px hsl(0 0% 0% / 0.00)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
  subtle: {
    id: 'subtle',
    name: 'Subtle Aqua',
    description: 'Soft cyan-teal subdued contrast theme',
    colors: {
      light: {
        background: '180 21.0526% 92.5490%',
        foreground: '188.8000 78.9474% 18.6275%',
        card: '180 23.8095% 95.8824%',
        'card-foreground': '188.8000 78.9474% 18.6275%',
        popover: '180 23.8095% 95.8824%',
        'popover-foreground': '188.8000 78.9474% 18.6275%',
        primary: '183.9706 91.8919% 29.0196%',
        'primary-foreground': '0 0% 100%',
        secondary: '180 28.8136% 88.4314%',
        'secondary-foreground': '188.8000 78.9474% 18.6275%',
        muted: '180 19.2308% 89.8039%',
        'muted-foreground': '184 31.2500% 37.6471%',
        accent: '184 38.4615% 84.7059%',
        'accent-foreground': '188.8000 78.9474% 18.6275%',
        destructive: '0 62.4490% 51.9608%',
        'destructive-foreground': '0 0% 100%',
        border: '185.7143 26.5823% 84.5098%',
        input: '180 28.8136% 88.4314%',
        ring: '183.9706 91.8919% 29.0196%',
        'sidebar-background': '186.3158 34.5455% 89.2157%',
        'sidebar-foreground': '188.8000 78.9474% 18.6275%',
        'sidebar-primary': '183.9706 91.8919% 29.0196%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '184 38.4615% 84.7059%',
        'sidebar-accent-foreground': '188.8000 78.9474% 18.6275%',
        'sidebar-border': '185.7143 26.5823% 84.5098%',
        'sidebar-ring': '183.9706 91.8919% 29.0196%',
        'color-1': '183.9706 91.8919% 29.0196%',
        'color-2': '183.5294 69.3878% 38.4314%',
        'color-3': '183.5556 55.1020% 48.0392%',
        'color-4': '183.7168 53.5545% 58.6275%',
        'color-5': '183.6145 54.9669% 70.3922%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.125rem',
        'font-sans': 'Source Code Pro, monospace',
        'font-serif': 'Source Code Pro, monospace',
        'font-mono': 'Source Code Pro, monospace',
        'shadow-x': '1px',
        'shadow-y': '1px',
        'shadow-blur': '2px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.15',
        'shadow-color': 'hsl(185 70% 30% / 0.15)',
        'shadow-2xs': '1px 1px 2px 0px hsl(185 70% 30% / 0.07)',
        'shadow-xs': '1px 1px 2px 0px hsl(185 70% 30% / 0.07)',
        'shadow-sm':
          '1px 1px 2px 0px hsl(185 70% 30% / 0.15), 1px 1px 2px -1px hsl(185 70% 30% / 0.15)',
        shadow: '1px 1px 2px 0px hsl(185 70% 30% / 0.15), 1px 1px 2px -1px hsl(185 70% 30% / 0.15)',
        'shadow-md':
          '1px 1px 2px 0px hsl(185 70% 30% / 0.15), 1px 2px 4px -1px hsl(185 70% 30% / 0.15)',
        'shadow-lg':
          '1px 1px 2px 0px hsl(185 70% 30% / 0.15), 1px 4px 6px -1px hsl(185 70% 30% / 0.15)',
        'shadow-xl':
          '1px 1px 2px 0px hsl(185 70% 30% / 0.15), 1px 8px 10px -1px hsl(185 70% 30% / 0.15)',
        'shadow-2xl': '1px 1px 2px 0px hsl(185 70% 30% / 0.38)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '196.3636 52.3810% 8.2353%',
        foreground: '180 77.1144% 60.5882%',
        card: '192 51.0204% 9.6078%',
        'card-foreground': '180 77.1144% 60.5882%',
        popover: '192 51.0204% 9.6078%',
        'popover-foreground': '180 77.1144% 60.5882%',
        primary: '180 77.1144% 60.5882%',
        'primary-foreground': '196.3636 52.3810% 8.2353%',
        secondary: '191.4286 58.8785% 20.9804%',
        'secondary-foreground': '180 77.1144% 60.5882%',
        muted: '192.8571 58.3333% 14.1176%',
        'muted-foreground': '180 50.6849% 42.9412%',
        accent: '191.4286 58.8785% 20.9804%',
        'accent-foreground': '180 77.1144% 60.5882%',
        destructive: '0 78.8991% 57.2549%',
        'destructive-foreground': '0 0% 94.9020%',
        border: '191.4286 58.8785% 20.9804%',
        input: '191.4286 58.8785% 20.9804%',
        ring: '180 77.1144% 60.5882%',
        'sidebar-background': '196.3636 52.3810% 8.2353%',
        'sidebar-foreground': '180 77.1144% 60.5882%',
        'sidebar-primary': '180 77.1144% 60.5882%',
        'sidebar-primary-foreground': '196.3636 52.3810% 8.2353%',
        'sidebar-accent': '191.4286 58.8785% 20.9804%',
        'sidebar-accent-foreground': '180 77.1144% 60.5882%',
        'sidebar-border': '191.4286 58.8785% 20.9804%',
        'sidebar-ring': '180 77.1144% 60.5882%',
        'color-1': '180 77.1144% 60.5882%',
        'color-2': '180 50.6849% 42.9412%',
        'color-3': '180 50.8197% 35.8824%',
        'color-4': '184.3478 57.9832% 23.3333%',
        'color-5': '185.2174 62.1622% 14.5098%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.125rem',
        'font-sans': 'Source Code Pro, monospace',
        'font-serif': 'Source Code Pro, monospace',
        'font-mono': 'Source Code Pro, monospace',
        'shadow-x': '1px',
        'shadow-y': '1px',
        'shadow-blur': '2px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.2',
        'shadow-color': 'hsl(180 70% 60% / 0.2)',
        'shadow-2xs': '1px 1px 2px 0px hsl(180 70% 60% / 0.10)',
        'shadow-xs': '1px 1px 2px 0px hsl(180 70% 60% / 0.10)',
        'shadow-sm':
          '1px 1px 2px 0px hsl(180 70% 60% / 0.20), 1px 1px 2px -1px hsl(180 70% 60% / 0.20)',
        shadow: '1px 1px 2px 0px hsl(180 70% 60% / 0.20), 1px 1px 2px -1px hsl(180 70% 60% / 0.20)',
        'shadow-md':
          '1px 1px 2px 0px hsl(180 70% 60% / 0.20), 1px 2px 4px -1px hsl(180 70% 60% / 0.20)',
        'shadow-lg':
          '1px 1px 2px 0px hsl(180 70% 60% / 0.20), 1px 4px 6px -1px hsl(180 70% 60% / 0.20)',
        'shadow-xl':
          '1px 1px 2px 0px hsl(180 70% 60% / 0.20), 1px 8px 10px -1px hsl(180 70% 60% / 0.20)',
        'shadow-2xl': '1px 1px 2px 0px hsl(180 70% 60% / 0.50)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
  blue: {
    id: 'blue',
    name: 'Ocean Blue',
    description: 'Cool and professional blue theme',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '222.2 47.4% 11.2%',
        muted: '210 40% 96.1%',
        'muted-foreground': '215.4 16.3% 46.9%',
        popover: '0 0% 100%',
        'popover-foreground': '222.2 47.4% 11.2%',
        border: '214.3 31.8% 91.4%',
        input: '214.3 31.8% 91.4%',
        card: '0 0% 100%',
        'card-foreground': '222.2 47.4% 11.2%',
        primary: '221.2 83.2% 53.3%',
        'primary-foreground': '210 40% 98%',
        secondary: '210 40% 96.1%',
        'secondary-foreground': '222.2 47.4% 11.2%',
        accent: '210 40% 96.1%',
        'accent-foreground': '222.2 47.4% 11.2%',
        destructive: '0 84.2% 60.2%',
        'destructive-foreground': '210 40% 98%',
        ring: '221.2 83.2% 53.3%',
        'sidebar-background': '210 100% 97%',
        'sidebar-foreground': '215 25% 27%',
        'sidebar-primary': '221.2 83.2% 53.3%',
        'sidebar-primary-foreground': '210 40% 98%',
        'sidebar-accent': '210 40% 93%',
        'sidebar-accent-foreground': '215 25% 27%',
        'sidebar-border': '214 32% 91%',
        'sidebar-ring': '221.2 83.2% 53.3%',
        'color-1': '221.2 83.2% 53.3%',
        'color-2': '212 100% 45%',
        'color-3': '216 100% 50%',
        'color-4': '210 100% 56%',
        'color-5': '204 100% 63%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',
      },
      dark: {
        background: '222 47% 11%',
        foreground: '213 31% 91%',
        muted: '223 47% 16%',
        'muted-foreground': '215.4 16.3% 56.9%',
        popover: '224 71% 4%',
        'popover-foreground': '213 31% 91%',
        border: '223 47% 16%',
        input: '223 47% 16%',
        card: '224 71% 4%',
        'card-foreground': '213 31% 91%',
        primary: '217 91% 60%',
        'primary-foreground': '222 47% 1.2%',
        secondary: '223 47% 16%',
        'secondary-foreground': '213 31% 91%',
        accent: '216 87% 21%',
        'accent-foreground': '213 31% 91%',
        destructive: '0 63% 31%',
        'destructive-foreground': '213 31% 91%',
        ring: '217 91% 60%',
        'sidebar-background': '224 71% 4%',
        'sidebar-foreground': '213 31% 91%',
        'sidebar-primary': '217 91% 60%',
        'sidebar-primary-foreground': '222 47% 11%',
        'sidebar-accent': '223 47% 16%',
        'sidebar-accent-foreground': '213 31% 91%',
        'sidebar-border': '223 47% 16%',
        'sidebar-ring': '217 91% 60%',
        'color-1': '217 91% 60%',
        'color-2': '212 100% 45%',
        'color-3': '216 100% 50%',
        'color-4': '210 100% 56%',
        'color-5': '204 100% 63%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',
      },
    },
  },
  green: {
    id: 'green',
    name: 'Forest Green',
    description: 'Natural and calming green theme',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '222.2 47.4% 11.2%',
        muted: '210 40% 96.1%',
        'muted-foreground': '215.4 16.3% 46.9%',
        popover: '0 0% 100%',
        'popover-foreground': '222.2 47.4% 11.2%',
        border: '214.3 31.8% 91.4%',
        input: '214.3 31.8% 91.4%',
        card: '0 0% 100%',
        'card-foreground': '222.2 47.4% 11.2%',
        primary: '142.1 76.2% 36.3%',
        'primary-foreground': '355.7 100% 97.3%',
        secondary: '210 40% 96.1%',
        'secondary-foreground': '222.2 47.4% 11.2%',
        accent: '210 40% 96.1%',
        'accent-foreground': '222.2 47.4% 11.2%',
        destructive: '0 84.2% 60.2%',
        'destructive-foreground': '210 40% 98%',
        ring: '142.1 76.2% 36.3%',
        'sidebar-background': '138 76% 97%',
        'sidebar-foreground': '140 9% 26%',
        'sidebar-primary': '142.1 76.2% 36.3%',
        'sidebar-primary-foreground': '355.7 100% 97.3%',
        'sidebar-accent': '138 40% 93%',
        'sidebar-accent-foreground': '140 9% 26%',
        'sidebar-border': '138 32% 91%',
        'sidebar-ring': '142.1 76.2% 36.3%',
        'color-1': '142.1 76.2% 36.3%',
        'color-2': '160 100% 45%',
        'color-3': '158 100% 50%',
        'color-4': '145 100% 56%',
        'color-5': '120 100% 63%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',
      },
      dark: {
        background: '140 50% 6%',
        foreground: '138 62% 93%',
        muted: '140 50% 10%',
        'muted-foreground': '140 5% 64%',
        popover: '140 50% 4%',
        'popover-foreground': '138 62% 93%',
        border: '140 50% 12%',
        input: '140 50% 12%',
        card: '140 50% 6%',
        'card-foreground': '138 62% 93%',
        primary: '142 86% 28%',
        'primary-foreground': '138 100% 98%',
        secondary: '140 50% 10%',
        'secondary-foreground': '138 62% 93%',
        accent: '140 50% 15%',
        'accent-foreground': '138 62% 93%',
        destructive: '0 63% 31%',
        'destructive-foreground': '138 62% 93%',
        ring: '142 86% 28%',
        'sidebar-background': '140 50% 4%',
        'sidebar-foreground': '138 62% 93%',
        'sidebar-primary': '142 86% 28%',
        'sidebar-primary-foreground': '138 100% 98%',
        'sidebar-accent': '140 50% 10%',
        'sidebar-accent-foreground': '138 62% 93%',
        'sidebar-border': '140 50% 12%',
        'sidebar-ring': '142 86% 28%',
        'color-1': '142 86% 28%',
        'color-2': '160 100% 45%',
        'color-3': '158 100% 50%',
        'color-4': '145 100% 56%',
        'color-5': '120 100% 63%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',
      },
    },
  },
  purple: {
    id: 'purple',
    name: 'Royal Purple',
    description: 'Elegant and sophisticated purple theme',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '222.2 47.4% 11.2%',
        muted: '210 40% 96.1%',
        'muted-foreground': '215.4 16.3% 46.9%',
        popover: '0 0% 100%',
        'popover-foreground': '222.2 47.4% 11.2%',
        border: '214.3 31.8% 91.4%',
        input: '214.3 31.8% 91.4%',
        card: '0 0% 100%',
        'card-foreground': '222.2 47.4% 11.2%',
        primary: '262.1 83.3% 57.8%',
        'primary-foreground': '210 40% 98%',
        secondary: '210 40% 96.1%',
        'secondary-foreground': '222.2 47.4% 11.2%',
        accent: '210 40% 96.1%',
        'accent-foreground': '222.2 47.4% 11.2%',
        destructive: '0 84.2% 60.2%',
        'destructive-foreground': '210 40% 98%',
        ring: '262.1 83.3% 57.8%',
        'sidebar-background': '270 100% 98%',
        'sidebar-foreground': '263 9% 26%',
        'sidebar-primary': '262.1 83.3% 57.8%',
        'sidebar-primary-foreground': '210 40% 98%',
        'sidebar-accent': '270 40% 93%',
        'sidebar-accent-foreground': '263 9% 26%',
        'sidebar-border': '270 32% 91%',
        'sidebar-ring': '262.1 83.3% 57.8%',
        'color-1': '262.1 83.3% 57.8%',
        'color-2': '280 100% 70%',
        'color-3': '270 100% 75%',
        'color-4': '260 100% 80%',
        'color-5': '250 100% 85%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',
      },
      dark: {
        background: '263 70% 8%',
        foreground: '263 40% 90%',
        muted: '263 70% 12%',
        'muted-foreground': '263 8% 60%',
        popover: '263 70% 6%',
        'popover-foreground': '263 40% 90%',
        border: '263 70% 15%',
        input: '263 70% 15%',
        card: '263 70% 8%',
        'card-foreground': '263 40% 90%',
        primary: '263 85% 70%',
        'primary-foreground': '263 70% 8%',
        secondary: '263 70% 12%',
        'secondary-foreground': '263 40% 90%',
        accent: '263 70% 18%',
        'accent-foreground': '263 40% 90%',
        destructive: '0 63% 31%',
        'destructive-foreground': '263 40% 90%',
        ring: '263 85% 70%',
        'sidebar-background': '263 70% 6%',
        'sidebar-foreground': '263 40% 90%',
        'sidebar-primary': '263 85% 70%',
        'sidebar-primary-foreground': '263 70% 8%',
        'sidebar-accent': '263 70% 12%',
        'sidebar-accent-foreground': '263 40% 90%',
        'sidebar-border': '263 70% 15%',
        'sidebar-ring': '263 85% 70%',
        'color-1': '263 85% 70%',
        'color-2': '280 100% 70%',
        'color-3': '270 100% 75%',
        'color-4': '260 100% 80%',
        'color-5': '250 100% 85%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',
      },
    },
  },
  haze: {
    id: 'haze',
    name: 'Haze',
    description: 'Soft purple-tinted theme with gentle gradients',
    colors: {
      light: {
        background: '260 23.0769% 97.4510%',
        foreground: '243.1579 13.6691% 27.2549%',
        muted: '258.0000 15.1515% 87.0588%',
        'muted-foreground': '247.5000 10.3448% 45.4902%',
        popover: '0 0% 100%',
        'popover-foreground': '243.1579 13.6691% 27.2549%',
        border: '258.7500 17.3913% 81.9608%',
        input: '260.0000 23.0769% 92.3529%',
        card: '0 0% 100%',
        'card-foreground': '243.1579 13.6691% 27.2549%',
        primary: '260.4000 22.9358% 57.2549%',
        'primary-foreground': '260 23.0769% 97.4510%',
        secondary: '258.9474 33.3333% 88.8235%',
        'secondary-foreground': '243.1579 13.6691% 27.2549%',
        accent: '342.4615 56.5217% 77.4510%',
        'accent-foreground': '343.4483 23.9669% 23.7255%',
        destructive: '0 62.1891% 60.5882%',
        'destructive-foreground': '260 23.0769% 97.4510%',
        ring: '260.4000 22.9358% 57.2549%',
        'sidebar-background': '260.0000 23.0769% 94.9020%',
        'sidebar-foreground': '243.1579 13.6691% 27.2549%',
        'sidebar-primary': '260.4000 22.9358% 57.2549%',
        'sidebar-primary-foreground': '260 23.0769% 97.4510%',
        'sidebar-accent': '342.4615 56.5217% 77.4510%',
        'sidebar-accent-foreground': '343.4483 23.9669% 23.7255%',
        'sidebar-border': '261.4286 18.4211% 85.0980%',
        'sidebar-ring': '260.4000 22.9358% 57.2549%',
        'color-1': '260.4000 22.9358% 57.2549%',
        'color-2': '342.4615 56.5217% 77.4510%',
        'color-3': '158.7692 31.4010% 59.4118%',
        'color-4': '35.7576 76.7442% 74.7059%',
        'color-5': '215.8209 54.4715% 75.8824%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.5rem',
        'shadow-x': '1px',
        'shadow-y': '2px',
        'shadow-blur': '5px',
        'shadow-spread': '1px',
        'shadow-opacity': '0.06',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '1px 2px 5px 1px hsl(0 0% 0% / 0.03)',
        'shadow-xs': '1px 2px 5px 1px hsl(0 0% 0% / 0.03)',
        'shadow-sm': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 1px 2px 0px hsl(0 0% 0% / 0.06)',
        shadow: '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 1px 2px 0px hsl(0 0% 0% / 0.06)',
        'shadow-md': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 2px 4px 0px hsl(0 0% 0% / 0.06)',
        'shadow-lg': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 4px 6px 0px hsl(0 0% 0% / 0.06)',
        'shadow-xl': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 8px 10px 0px hsl(0 0% 0% / 0.06)',
        'shadow-2xl': '1px 2px 5px 1px hsl(0 0% 0% / 0.15)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '250.9091 18.6441% 11.5686%',
        foreground: '250.0000 36.0000% 90.1961%',
        muted: '254.1176 20.9877% 15.8824%',
        'muted-foreground': '258.9474 10.3825% 64.1176%',
        popover: '251.2500 20% 15.6863%',
        'popover-foreground': '250.0000 36.0000% 90.1961%',
        border: '252 18.5185% 21.1765%',
        input: '249.4737 19.5876% 19.0196%',
        card: '251.2500 20% 15.6863%',
        'card-foreground': '250.0000 36.0000% 90.1961%',
        primary: '263.0769 32.5000% 68.6275%',
        'primary-foreground': '250.9091 18.6441% 11.5686%',
        secondary: '254.4828 14.8718% 38.2353%',
        'secondary-foreground': '250.0000 36.0000% 90.1961%',
        accent: '271.7647 15.5963% 21.3725%',
        'accent-foreground': '345.5172 69.0476% 83.5294%',
        destructive: '0 68.6747% 67.4510%',
        'destructive-foreground': '250.9091 18.6441% 11.5686%',
        ring: '263.0769 32.5000% 68.6275%',
        'sidebar-background': '252 20.0000% 9.8039%',
        'sidebar-foreground': '250.0000 36.0000% 90.1961%',
        'sidebar-primary': '263.0769 32.5000% 68.6275%',
        'sidebar-primary-foreground': '250.9091 18.6441% 11.5686%',
        'sidebar-accent': '271.7647 15.5963% 21.3725%',
        'sidebar-accent-foreground': '345.5172 69.0476% 83.5294%',
        'sidebar-border': '249.4737 19.5876% 19.0196%',
        'sidebar-ring': '263.0769 32.5000% 68.6275%',
        'color-1': '263.0769 32.5000% 68.6275%',
        'color-2': '345.5172 69.0476% 83.5294%',
        'color-3': '158.7692 31.4010% 59.4118%',
        'color-4': '35.7576 76.7442% 74.7059%',
        'color-5': '215.8209 54.4715% 75.8824%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',

        radius: '0.5rem',
        'shadow-x': '1px',
        'shadow-y': '2px',
        'shadow-blur': '5px',
        'shadow-spread': '1px',
        'shadow-opacity': '0.06',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '1px 2px 5px 1px hsl(0 0% 0% / 0.03)',
        'shadow-xs': '1px 2px 5px 1px hsl(0 0% 0% / 0.03)',
        'shadow-sm': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 1px 2px 0px hsl(0 0% 0% / 0.06)',
        shadow: '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 1px 2px 0px hsl(0 0% 0% / 0.06)',
        'shadow-md': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 2px 4px 0px hsl(0 0% 0% / 0.06)',
        'shadow-lg': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 4px 6px 0px hsl(0 0% 0% / 0.06)',
        'shadow-xl': '1px 2px 5px 1px hsl(0 0% 0% / 0.06), 1px 8px 10px 0px hsl(0 0% 0% / 0.06)',
        'shadow-2xl': '1px 2px 5px 1px hsl(0 0% 0% / 0.15)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
  papermint: {
    id: 'papermint',
    name: 'Papermint',
    description: 'Vibrant pink and mint theme with distinctive hard shadows',
    colors: {
      light: {
        background: '330 47.0588% 93.3333%',
        foreground: '0 0% 35.6863%',
        muted: '190.5263 58.7629% 80.9804%',
        'muted-foreground': '0 0% 47.8431%',
        popover: '0 0% 100%',
        'popover-foreground': '0 0% 35.6863%',
        border: '325.5814 57.8475% 56.2745%',
        input: '0 0% 89.4118%',
        card: '41.5385 92.8571% 89.0196%',
        'card-foreground': '0 0% 35.6863%',
        primary: '325.5814 57.8475% 56.2745%',
        'primary-foreground': '0 0% 100%',
        secondary: '181.6901 43.5583% 68.0392%',
        'secondary-foreground': '0 0% 20%',
        accent: '42.1429 91.3043% 81.9608%',
        'accent-foreground': '0 0% 20%',
        destructive: '359.5652 92.0000% 70.5882%',
        'destructive-foreground': '0 0% 100%',
        ring: '330 70.2381% 67.0588%',
        'sidebar-background': '326.2500 69.5652% 90.9804%',
        'sidebar-foreground': '0 0% 20%',
        'sidebar-primary': '330.3659 81.1881% 60.3922%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '327.4074 87.0968% 81.7647%',
        'sidebar-accent-foreground': '0 0% 20%',
        'sidebar-border': '268.6957 100.0000% 95.4902%',
        'sidebar-ring': '330.3659 81.1881% 60.3922%',
        'color-1': '330 70.2381% 67.0588%',
        'color-2': '190.2128 61.8421% 70.1961%',
        'color-3': '42.1429 91.3043% 81.9608%',
        'color-4': '329.6386 77.5701% 79.0196%',
        'color-5': '330.6294 64.1256% 56.2745%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.4rem',
        'shadow-x': '3px',
        'shadow-y': '3px',
        'shadow-blur': '0px',
        'shadow-spread': '0px',
        'shadow-opacity': '1.0',
        'shadow-color': 'hsl(325.78 58.18% 56.86% / 0.5)',
        'shadow-2xs': '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 0.50)',
        'shadow-xs': '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 0.50)',
        'shadow-sm':
          '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 1.00), 3px 1px 2px -1px hsl(325.7800 58.1800% 56.8600% / 1.00)',
        shadow:
          '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 1.00), 3px 1px 2px -1px hsl(325.7800 58.1800% 56.8600% / 1.00)',
        'shadow-md':
          '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 1.00), 3px 2px 4px -1px hsl(325.7800 58.1800% 56.8600% / 1.00)',
        'shadow-lg':
          '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 1.00), 3px 4px 6px -1px hsl(325.7800 58.1800% 56.8600% / 1.00)',
        'shadow-xl':
          '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 1.00), 3px 8px 10px -1px hsl(325.7800 58.1800% 56.8600% / 1.00)',
        'shadow-2xl': '3px 3px 0px 0px hsl(325.7800 58.1800% 56.8600% / 2.50)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '201.4286 43.7500% 12.5490%',
        foreground: '333.7500 40.0000% 92.1569%',
        muted: '214.2857 8.8608% 15.4902%',
        'muted-foreground': '346.3636 55.0000% 76.4706%',
        popover: '201.4286 33.3333% 16.4706%',
        'popover-foreground': '333.7500 40.0000% 92.1569%',
        border: '206.1538 28.0576% 27.2549%',
        input: '200.6897 31.1828% 18.2353%',
        card: '201.4286 33.3333% 16.4706%',
        'card-foreground': '333.7500 40.0000% 92.1569%',
        primary: '42.1429 91.3043% 81.9608%',
        'primary-foreground': '201.4286 43.7500% 12.5490%',
        secondary: '346.3636 55.0000% 76.4706%',
        'secondary-foreground': '201.4286 43.7500% 12.5490%',
        accent: '338.4000 39.6825% 62.9412%',
        'accent-foreground': '333.7500 40.0000% 92.1569%',
        destructive: '328.4211 70.3704% 62.9412%',
        'destructive-foreground': '201.4286 43.7500% 12.5490%',
        ring: '184.1176 41.1290% 51.3725%',
        'sidebar-background': '202.5000 42.8571% 10.9804%',
        'sidebar-foreground': '220.0000 14.2857% 95.8824%',
        'sidebar-primary': '330.3659 81.1881% 60.3922%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '327.4074 87.0968% 81.7647%',
        'sidebar-accent-foreground': '215.0000 27.9070% 16.8627%',
        'sidebar-border': '216.9231 19.1176% 26.6667%',
        'sidebar-ring': '330.3659 81.1881% 60.3922%',
        'color-1': '184.1176 41.1290% 51.3725%',
        'color-2': '346.3636 55.0000% 76.4706%',
        'color-3': '338.4000 39.6825% 62.9412%',
        'color-4': '191.2941 64.8855% 25.6863%',
        'color-5': '214.2857 8.8608% 15.4902%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.4rem',
        'shadow-x': '3px',
        'shadow-y': '3px',
        'shadow-blur': '0px',
        'shadow-spread': '0px',
        'shadow-opacity': '1.0',
        'shadow-color': '#324859',
        'shadow-2xs': '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 0.50)',
        'shadow-xs': '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 0.50)',
        'shadow-sm':
          '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 1.00), 3px 1px 2px -1px hsl(206.1538 28.0576% 27.2549% / 1.00)',
        shadow:
          '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 1.00), 3px 1px 2px -1px hsl(206.1538 28.0576% 27.2549% / 1.00)',
        'shadow-md':
          '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 1.00), 3px 2px 4px -1px hsl(206.1538 28.0576% 27.2549% / 1.00)',
        'shadow-lg':
          '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 1.00), 3px 4px 6px -1px hsl(206.1538 28.0576% 27.2549% / 1.00)',
        'shadow-xl':
          '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 1.00), 3px 8px 10px -1px hsl(206.1538 28.0576% 27.2549% / 1.00)',
        'shadow-2xl': '3px 3px 0px 0px hsl(206.1538 28.0576% 27.2549% / 2.50)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
  hazelnut: {
    id: 'hazelnut',
    name: 'Hazelnut',
    description: 'Warm brown and purple theme with soft shadows',
    colors: {
      light: {
        background: '300 33.3333% 97.0588%',
        foreground: '296 55.5556% 21.1765%',
        muted: '310.5882 48.5714% 93.1373%',
        'muted-foreground': '295.5224 32.6829% 40.1961%',
        popover: '0 0% 100%',
        'popover-foreground': '296 55.5556% 21.1765%',
        border: '304.8000 60.9756% 83.9216%',
        input: '317.3684 44.1860% 83.1373%',
        card: '300 33.3333% 97.0588%',
        'card-foreground': '296 55.5556% 21.1765%',
        primary: '333.2673 42.9787% 46.0784%',
        'primary-foreground': '0 0% 100%',
        secondary: '314.6667 61.6438% 85.6863%',
        'secondary-foreground': '295.8333 40.9091% 34.5098%',
        accent: '314.6667 61.6438% 85.6863%',
        'accent-foreground': '295.8333 40.9091% 34.5098%',
        destructive: '357.6923 43.6975% 46.6667%',
        'destructive-foreground': '0 0% 100%',
        ring: '333.3333 71.4286% 50.5882%',
        'sidebar-background': '290.0000 50% 92.9412%',
        'sidebar-foreground': '327.2000 77.3196% 38.0392%',
        'sidebar-primary': '240 9.8039% 30%',
        'sidebar-primary-foreground': '320.0000 47.3684% 96.2745%',
        'sidebar-accent': '60 6.6667% 97.0588%',
        'sidebar-accent-foreground': '240 9.8039% 30%',
        'sidebar-border': '20.0000 7.3171% 91.9608%',
        'sidebar-ring': '333.3333 71.4286% 50.5882%',
        'color-1': '318.4358 70.1961% 50%',
        'color-2': '272.3353 82.2660% 39.8039%',
        'color-3': '197.3333 36.5854% 24.1176%',
        'color-4': '43.1250 73.5632% 65.8824%',
        'color-5': '27.1233 86.9048% 67.0588%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',

        radius: '0.5rem',
        'shadow-x': '0',
        'shadow-y': '1px',
        'shadow-blur': '3px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.1',
        'shadow-color': 'oklch(0 0 0)',
        'shadow-2xs': '0 1px 3px 0px hsl(0 0% 0% / 0.05)',
        'shadow-xs': '0 1px 3px 0px hsl(0 0% 0% / 0.05)',
        'shadow-sm': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        shadow: '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        'shadow-md': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)',
        'shadow-lg': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)',
        'shadow-xl': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)',
        'shadow-2xl': '0 1px 3px 0px hsl(0 0% 0% / 0.25)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '270 14.7059% 13.3333%',
        foreground: '272.3077 28.2609% 81.9608%',
        muted: '272.7273 13.9241% 15.4902%',
        'muted-foreground': '268.8000 20.6612% 76.2745%',
        popover: '320 23.0769% 5.0980%',
        'popover-foreground': '325.7143 33.3333% 95.8824%',
        border: '326.6667 8.2569% 21.3725%',
        input: '312 8.7719% 22.3529%',
        card: '270 13.6364% 17.2549%',
        'card-foreground': '324.5455 23.4043% 81.5686%',
        primary: '332.0245 100% 31.9608%',
        'primary-foreground': '329.3617 59.4937% 84.5098%',
        secondary: '273.7500 15.0943% 20.7843%',
        'secondary-foreground': '270.0000 30.2326% 83.1373%',
        accent: '272.1429 20.2899% 27.0588%',
        'accent-foreground': '325.7143 33.3333% 95.8824%',
        destructive: '350.6250 50% 12.5490%',
        'destructive-foreground': '0 0% 100%',
        ring: '333.3333 71.4286% 50.5882%',
        'sidebar-background': '308.5714 17.0732% 8.0392%',
        'sidebar-foreground': '327.2727 26.1905% 83.5294%',
        'sidebar-primary': '224.2781 76.3265% 48.0392%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '318.4615 20.6349% 12.3529%',
        'sidebar-accent-foreground': '240 4.7619% 95.8824%',
        'sidebar-border': '0 0% 0%',
        'sidebar-ring': '333.3333 71.4286% 50.5882%',
        'color-1': '333.2673 42.9787% 46.0784%',
        'color-2': '273.3333 54.7826% 54.9020%',
        'color-3': '30.0000 80% 54.9020%',
        'color-4': '280 64.7059% 60%',
        'color-5': '339.7674 74.7826% 54.9020%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',

        radius: '0.5rem',
        'shadow-x': '0',
        'shadow-y': '1px',
        'shadow-blur': '3px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.1',
        'shadow-color': 'oklch(0 0 0)',
        'shadow-2xs': '0 1px 3px 0px hsl(0 0% 0% / 0.05)',
        'shadow-xs': '0 1px 3px 0px hsl(0 0% 0% / 0.05)',
        'shadow-sm': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        shadow: '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        'shadow-md': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 2px 4px -1px hsl(0 0% 0% / 0.10)',
        'shadow-lg': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 4px 6px -1px hsl(0 0% 0% / 0.10)',
        'shadow-xl': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 8px 10px -1px hsl(0 0% 0% / 0.10)',
        'shadow-2xl': '0 1px 3px 0px hsl(0 0% 0% / 0.25)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
  spglobal: {
    id: 'spglobal',
    name: 'S&P Global',
    description: 'Professional corporate theme with S&P red primary and neutral gray accents',
    colors: {
      light: {
        background: '0 0% 100%',
        foreground: '0 0% 10.2%',
        muted: '0 0% 96.5%',
        'muted-foreground': '0 0% 45%',
        popover: '0 0% 100%',
        'popover-foreground': '0 0% 10.2%',
        border: '0 0% 90%',
        input: '0 0% 96.5%',
        card: '0 0% 100%',
        'card-foreground': '0 0% 10.2%',
        primary: '348.3 100% 42%',
        'primary-foreground': '0 0% 100%',
        secondary: '0 0% 96.5%',
        'secondary-foreground': '0 0% 10.2%',
        accent: '0 0% 90%',
        'accent-foreground': '0 0% 10.2%',
        destructive: '0 84.2% 60.2%',
        'destructive-foreground': '0 0% 100%',
        ring: '0 0% 45%',
        'sidebar-background': '0 0% 98%',
        'sidebar-foreground': '0 0% 10.2%',
        'sidebar-primary': '348.3 100% 42%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '0 0% 90%',
        'sidebar-accent-foreground': '0 0% 10.2%',
        'sidebar-border': '0 0% 92%',
        'sidebar-ring': '348.3 100% 42%',
        'color-1': '348.3 100% 42%',
        'color-2': '0 0% 20%',
        'color-3': '0 0% 40%',
        'color-4': '0 0% 60%',
        'color-5': '0 0% 90%',
        'chart-1': '0.588 0.243 264.376',
        'chart-2': '0.596 0.17 162.48',
        'chart-3': '0.669 0.188 70.08',
        'chart-4': '0.527 0.265 303.9',
        'chart-5': '0.545 0.246 16.439',
        radius: '0.375rem',
        'shadow-x': '0',
        'shadow-y': '1px',
        'shadow-blur': '3px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.1',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '0 1px 2px 0px hsl(0 0% 0% / 0.05)',
        'shadow-xs': '0 1px 2px 0px hsl(0 0% 0% / 0.05)',
        'shadow-sm': '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        shadow: '0 1px 3px 0px hsl(0 0% 0% / 0.10), 0 1px 2px -1px hsl(0 0% 0% / 0.10)',
        'shadow-md': '0 4px 6px -1px hsl(0 0% 0% / 0.10), 0 2px 4px -2px hsl(0 0% 0% / 0.10)',
        'shadow-lg': '0 10px 15px -3px hsl(0 0% 0% / 0.10), 0 4px 6px -4px hsl(0 0% 0% / 0.10)',
        'shadow-xl': '0 20px 25px -5px hsl(0 0% 0% / 0.10), 0 8px 10px -6px hsl(0 0% 0% / 0.10)',
        'shadow-2xl': '0 25px 50px -12px hsl(0 0% 0% / 0.25)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
      dark: {
        background: '0 0% 10.2%',
        foreground: '0 0% 100%',
        muted: '0 0% 15%',
        'muted-foreground': '0 0% 65%',
        popover: '0 0% 12%',
        'popover-foreground': '0 0% 100%',
        border: '0 0% 20%',
        input: '0 0% 15%',
        card: '0 0% 12%',
        'card-foreground': '0 0% 100%',
        primary: '348.3 100% 42%',
        'primary-foreground': '0 0% 100%',
        secondary: '0 0% 15%',
        'secondary-foreground': '0 0% 100%',
        accent: '0 0% 20%',
        'accent-foreground': '0 0% 100%',
        destructive: '0 62.8% 30.6%',
        'destructive-foreground': '0 0% 100%',
        ring: '0 0% 60%',
        'sidebar-background': '0 0% 8%',
        'sidebar-foreground': '0 0% 100%',
        'sidebar-primary': '348.3 100% 42%',
        'sidebar-primary-foreground': '0 0% 100%',
        'sidebar-accent': '0 0% 20%',
        'sidebar-accent-foreground': '0 0% 100%',
        'sidebar-border': '0 0% 18%',
        'sidebar-ring': '348.3 100% 42%',
        'color-1': '348.3 100% 42%',
        'color-2': '0 0% 80%',
        'color-3': '0 0% 60%',
        'color-4': '0 0% 40%',
        'color-5': '0 0% 20%',
        'chart-1': '0.488 0.243 264.376',
        'chart-2': '0.696 0.17 162.48',
        'chart-3': '0.769 0.188 70.08',
        'chart-4': '0.627 0.265 303.9',
        'chart-5': '0.645 0.246 16.439',
        radius: '0.375rem',
        'shadow-x': '0',
        'shadow-y': '1px',
        'shadow-blur': '3px',
        'shadow-spread': '0px',
        'shadow-opacity': '0.25',
        'shadow-color': 'hsl(0 0% 0%)',
        'shadow-2xs': '0 1px 2px 0px hsl(0 0% 0% / 0.25)',
        'shadow-xs': '0 1px 2px 0px hsl(0 0% 0% / 0.25)',
        'shadow-sm': '0 1px 3px 0px hsl(0 0% 0% / 0.25), 0 1px 2px -1px hsl(0 0% 0% / 0.25)',
        shadow: '0 1px 3px 0px hsl(0 0% 0% / 0.25), 0 1px 2px -1px hsl(0 0% 0% / 0.25)',
        'shadow-md': '0 4px 6px -1px hsl(0 0% 0% / 0.25), 0 2px 4px -2px hsl(0 0% 0% / 0.25)',
        'shadow-lg': '0 10px 15px -3px hsl(0 0% 0% / 0.25), 0 4px 6px -4px hsl(0 0% 0% / 0.25)',
        'shadow-xl': '0 20px 25px -5px hsl(0 0% 0% / 0.25), 0 8px 10px -6px hsl(0 0% 0% / 0.25)',
        'shadow-2xl': '0 25px 50px -12px hsl(0 0% 0% / 0.5)',
        'tracking-normal': '0em',
        spacing: '0.25rem',
      },
    },
  },
};

export const getThemeById = (id: string): Theme | undefined => themes[id];

export const getAllThemes = (): Theme[] => Object.values(themes);

export function getThemeColors(themeId: string, mode: ThemeMode): BaseColors {
  const theme = getThemeById(themeId);
  if (!theme) {
    throw new Error(`Theme with id "${themeId}" not found`);
  }

  return theme.colors[mode];
}

export function applyThemeToDocument(themeId: string, mode: ThemeMode): void {
  const colors = getThemeColors(themeId, mode);
  const root = document.documentElement;

  root.classList.remove('light', 'dark');

  root.classList.add(mode);

  const previousKeysAttr = root.getAttribute('data-theme-keys');
  if (previousKeysAttr) {
    const previousKeys = previousKeysAttr.split(',').filter(Boolean);
    previousKeys.forEach(k => {
      if (!(k in colors)) {
        root.style.removeProperty(`--${k}`);
        if (k === 'shadow' || k.startsWith('shadow-')) {
          const shadowClass = k === 'shadow' ? 'shadow' : k;
          root.style.removeProperty(`--tw-${shadowClass}`);
          root.style.removeProperty(`--tw-${shadowClass}-colored`);
        }
      }
    });
  }

  const appliedKeys: string[] = [];
  Object.entries(colors).forEach(([key, value]) => {
    if (value !== undefined) {
      appliedKeys.push(key);
      root.style.setProperty(`--${key}`, value);

      if (key === 'shadow') {
        root.style.setProperty('--tw-shadow', value);
        root.style.setProperty('--tw-shadow-colored', value);
        root.style.setProperty('--tw-shadow-box-shadow', value);
      }

      if (key.startsWith('shadow-') || key === 'shadow') {
        const shadowClass = key === 'shadow' ? 'shadow' : key;
        root.style.setProperty(`--tw-${shadowClass}`, value);
        root.style.setProperty(`--tw-${shadowClass}-colored`, value);
      }
    }
  });

  root.setAttribute('data-theme-keys', appliedKeys.join(','));

  let styleElement = document.getElementById('theme-shadow-override');
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'theme-shadow-override';
    document.head.appendChild(styleElement);
  }

  let shadowCSS = '';

  Object.entries(colors).forEach(([key, value]) => {
    if (key.startsWith('shadow') || key === 'shadow') {
      if (value) {
        const className = key === 'shadow' ? 'shadow' : key;
        shadowCSS += `
          .${className} {
            --tw-shadow: ${value} !important;
            --tw-shadow-colored: ${value} !important;
            box-shadow: ${value} !important;
          }
        `;
      }
    }
  });

  styleElement.textContent = shadowCSS;

  const FONT_LINK_ID = 'dynamic-font-source-code-pro';
  const needsSourceCodePro = themeId === 'subtle';
  const existingFontLink = document.getElementById(FONT_LINK_ID);
  if (needsSourceCodePro && !existingFontLink) {
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  } else if (!needsSourceCodePro && existingFontLink) {
    existingFontLink.remove();
  }
}

export const defaultThemeConfig: ThemeConfig = {
  theme: 'default',
  mode: 'light',
};
